'use client';
import { Loader } from '@mantine/core';
import { useClickOutside } from '@mantine/hooks';
import { OrgTypes } from '@scaleup/models';
import { usePasswordless } from 'amazon-cognito-passwordless-auth/react';
import Image from 'next/image';
import { useRouter } from 'next/navigation';
import { useState } from 'react';
import { useGetOrgProfileImageQuery, useGetUserQuery } from '../api';
import { SupplierDemoWrapper } from '../features';
import { safeImage } from '../utils';
import { IconCactusFilled, IconCookieFilled, IconLock, IconLockFilled, IconUserShield } from '@tabler/icons-react';

const ORG_TYPE_PREFIXES = {
  [OrgTypes.capitalProvider]: 'capital-provider',
  [OrgTypes.enterpriseCustomer]: 'enterprise',
  [OrgTypes.supplier]: 'supplier',
};

interface IProps {
  profileRoute?: string;
  settingsRoute?: string;
}

export const PLACEHOLDER_PROFILE =
  'data:image/png;base64,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';

export const UsersSettingsDropDown = ({
  profileRoute,
  settingsRoute,
}: IProps) => {
  const router = useRouter();
  const { data, isLoading: isUserLoading } = useGetUserQuery();
  const { data: profileImageData, isLoading: isProfileLoading } =
    useGetOrgProfileImageQuery(data?.orgId || '', { skip: !data?.orgId });
  const [open, setOpen] = useState<boolean>(false);
  const { signOut } = usePasswordless();
  const reqinProgress = Boolean(isUserLoading || isProfileLoading);
  const ref = useClickOutside(() => setOpen(false));

  return (
    <div className="relative">
      <button
        onClick={setOpen.bind(null, !open)}
        className="flex gap-1 p-1 items-center rounded-[100px] bg-white/75"
      >
        {reqinProgress ? (
          <Loader size={40} />
        ) : (
          <Image
            src={safeImage(profileImageData?.image) || PLACEHOLDER_PROFILE}
            width={40}
            height={40}
            alt="Avatar"
          />
        )}
        <Image
          src="/icons/chevron_down-blue.svg"
          alt=""
          width={24}
          height={24}
        />
      </button>

      <div
        ref={ref}
        className={`rounded-[1rem] shadow-notification absolute bg-white w-[175px] right-0 top-[54px] z-10 ${open ? 'block' : 'hidden'} flex flex-col gap-2 p-4`}
      >
        <SupplierDemoWrapper>
          <button
            onClick={() => {
              router.push(profileRoute || `/profile`);
            }}
            className="flex gap-2 p-2 items-center"
          >
            <Image src="/icons/user.svg" alt="Logout" width={24} height={24} />
            <span>My Profile</span>
          </button>
        </SupplierDemoWrapper>
        <button
          onClick={() => {
            // Dispatch event to open cookie settings
            if (typeof window !== 'undefined') {
              window.dispatchEvent(new CustomEvent('openCookieSettings'));
            }
            setOpen(false);
          }}
          className="flex p-2 items-center hover:bg-gray-50 rounded transition-colors"
        >
          <IconCookieFilled
            size={40}
            className="text-text-primary"
            color='#4298B0'
           />
          <span>Cookie Settings</span>
        </button>
        <button
          onClick={() => {
            router.push('/privacy-policy');
            setOpen(false);
          }}
          className="flex gap-2 p-2 items-center hover:bg-gray-50 rounded transition-colors"
        >
          <IconLockFilled
            size={40}
            className="text-text-primary"
            color='#4298B0'
           />
          <span>Privacy Policy</span>
        </button>
        <button
          onClick={async () => {
            signOut();
            router.push('/');
          }}
          className="flex gap-2 p-2 items-center"
        >
          <Image src="/icons/logout.svg" alt="Logout" width={24} height={24} />
          <span>Logout</span>
        </button>
      </div>
    </div>
  );
};
